/**
 * @author       justin.jeong@zumper.com (Justin Jeong)
 * @copyright    Copyright (c) 2019, Zumper
 * @description  loadDetailPriceData thunk
 */

import { definitelyError, isFailedToFetchError, isPriceDataResourceError } from '@zcode/nr-utils'
import * as Api from '@zcode/nr/es/shared/api'
import type { Property } from '@zcode/state-type'
import type { Dispatch } from 'redux'
import { logger } from '../../../logger'
import type { GetState } from '../../types'
import {
  clearHoodPriceChartData,
  clearPriceChartData,
  doneFetchingCityPriceChartData,
  doneFetchingHoodPriceChartData,
  setCityPriceChartData,
  setHoodPriceChartData,
  startFetchingCityPriceChartData,
  startFetchingHoodPriceChartData,
} from '../actions/detail'
import {
  selectDetailCityPriceCurrentKey,
  selectDetailCityPriceFetchingKey,
  selectDetailHoodPriceCurrentKey,
  selectDetailHoodPriceFetchingKey,
} from '../selectors'

const loadDetailPriceData =
  ({ entity }: { entity: Pick<Property, 'cityState' | 'cityStateHood'> | null | undefined }) =>
  async (dispatch: Dispatch, getState: GetState) => {
    const cityCurrentKey = selectDetailCityPriceCurrentKey(getState())
    const hoodCurrentKey = selectDetailHoodPriceCurrentKey(getState())
    const cityKey = entity?.cityState
    const hoodKey = entity?.cityStateHood
    const promises: Array<Promise<void>> = []
    // on detail page, check cityStateHood from entity
    // first call for city price data, second call for the hood data

    // check currentKey to disregard subsequent calls with the same slug
    if (cityKey && cityKey !== cityCurrentKey) {
      dispatch(clearPriceChartData())
      dispatch(startFetchingCityPriceChartData({ key: cityKey }))
      promises.push(
        Api.newPriceData
          .get({ region: cityKey })
          .then((response) => {
            const cityFetchingKey = selectDetailCityPriceFetchingKey(getState())
            if (response.data && cityKey === cityFetchingKey) {
              dispatch(setCityPriceChartData({ priceChartData: response.data, key: cityKey }))
            }
          })
          .catch((err: unknown) => {
            if (!isPriceDataResourceError(err) && !isFailedToFetchError(err)) {
              const msg = 'Failed to get columnar price data for city in loadDetailPriceData'
              logger.error(msg, entity, definitelyError(msg, err))
            }
          })
          .finally(() => {
            dispatch(doneFetchingCityPriceChartData())
          })
      )
    }
    // repeat for hood
    if (hoodKey && hoodKey !== hoodCurrentKey) {
      dispatch(startFetchingHoodPriceChartData({ key: hoodKey }))
      promises.push(
        Api.newPriceData
          .get({ region: hoodKey })
          .then((response) => {
            const hoodFetchingKey = selectDetailHoodPriceFetchingKey(getState())
            if (response.data && hoodKey === hoodFetchingKey) {
              dispatch(setHoodPriceChartData({ priceChartData: response.data, key: hoodKey }))
            }
          })
          .catch((err: unknown) => {
            if (!isPriceDataResourceError(err) && !isFailedToFetchError(err)) {
              const msg = 'Failed to get columnar price data for hood in loadDetailPriceData'
              logger.error(msg, entity, definitelyError(msg, err))
            }
            dispatch(clearHoodPriceChartData())
          })
          .finally(() => {
            dispatch(doneFetchingHoodPriceChartData())
          })
      )
    } else if (!hoodKey) {
      dispatch(clearHoodPriceChartData())
    }
    await Promise.all(promises)
  }

export default loadDetailPriceData
